import moment from 'moment-timezone'
import React, { useCallback, useReducer } from 'react'
import { makeStyles, MenuItem, Paper, Select } from '@material-ui/core'

import { ContainedButton } from 'civic-champs-shared'

import PageHeader from 'App/components/PageHeader'
import useGetProcessStatus, { ProcessWorkerStatus } from './useGetProcessStatus'
import { useAutorefresh } from './useAutorefresh'
import { groupWorkersByInstance, resolveWorkerLabel } from './workerGrouping.util'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  summary: {
    margin: '0 0 12px 0',
    color: '#555',
    fontSize: '14px',
  },
  statusContainer: {
    padding: '15px',
  },
  statusFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    '& th, & td': {
      textAlign: 'left',
      padding: '8px 10px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'top',
    },
    '& th': {
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
  },
  metricOk: {
    color: '#2e7d32',
    fontWeight: 600,
  },
  metricWarn: {
    color: '#ed6c02',
    fontWeight: 600,
  },
  metricDanger: {
    color: '#c62828',
    fontWeight: 700,
  },
  instanceHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 700,
    color: '#333',
  },
  outlinedSelect: {
    fontFamily: 'Nunito',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: '0.4px',
    borderRadius: '63px !important',
    border: '2px solid #0F5DB5',
    padding: '5px 10px 5px 15px',
    minHeight: '34px',
    boxSizing: 'border-box',
    '& ~ fieldset': {
      border: 0,
    },
  },
  outlinedSelectControl: {
    '& .MuiOutlinedInput-root': {
      minHeight: '34px',
      boxSizing: 'border-box',
    },
    '& .MuiSelect-selectMenu': {
      display: 'flex',
      alignItems: 'center',
      minHeight: '16px',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  refreshButton: {
    padding: '5px 15px !important',
    lineHeight: '16px',
    fontWeight: 700,
    letterSpacing: '0.4px',
    borderRadius: '63px',
    minHeight: '34px',
    boxSizing: 'border-box',
    boxShadow: 'none',
  },
  footerControls: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
})

const AUTO_REFRESH_OPTIONS = [
  { value: 10, label: '10s' },
  { value: 30, label: '30s' },
  { value: 60, label: '60s' },
  { value: -1, label: 'Never' },
]

/** Green below 50%, yellow 50–79%, red 80%+. */
const METRIC_OK_MAX_PERCENT = 50
const METRIC_WARN_MAX_PERCENT = 80

const EVENT_LOOP_REFERENCE_MS = 200
const HANDLER_REFERENCE = 10

type MetricLevel = 'ok' | 'warn' | 'danger'

const percentLevel = (percent: number): MetricLevel => {
  if (percent < METRIC_OK_MAX_PERCENT) return 'ok'
  if (percent < METRIC_WARN_MAX_PERCENT) return 'warn'
  return 'danger'
}

const ratioLevel = (value: number, max: number): MetricLevel => {
  if (max <= 0) return 'ok'
  return percentLevel((value / max) * 100)
}

const metricClassName = (classes: ReturnType<typeof useStyles>, level: MetricLevel) => {
  switch (level) {
    case 'ok':
      return classes.metricOk
    case 'warn':
      return classes.metricWarn
    case 'danger':
      return classes.metricDanger
  }
}

const cpuLevel = (cpuPercent: number | null): MetricLevel | null => {
  if (cpuPercent == null) return null
  return percentLevel(cpuPercent)
}

const dbPoolLevel = (worker: ProcessWorkerStatus): MetricLevel => {
  const { active, waiting, max } = worker.dbPool
  if (waiting > 0) {
    return active >= max ? 'danger' : 'warn'
  }
  return ratioLevel(active, max)
}

const handlerLevel = (worker: ProcessWorkerStatus): MetricLevel =>
  ratioLevel(worker.handlerRunning + worker.handlerQueued, HANDLER_REFERENCE)

interface State {
  autorefreshRate: number
  lastUpdated?: Date
}

const reducer = (state: State, action: Partial<State>) => ({ ...state, ...action })
const initialState = { autorefreshRate: 10 }

const formatUptime = (seconds: number) => {
  const d = moment.duration(seconds, 'seconds')
  if (d.asHours() >= 1) return `${Math.floor(d.asHours())}h ${d.minutes()}m`
  if (d.asMinutes() >= 1) return `${d.minutes()}m ${d.seconds()}s`
  return `${d.seconds()}s`
}

const formatDbPool = (worker: ProcessWorkerStatus) => {
  const { active, idle, waiting, max } = worker.dbPool
  return `${active}/${max} active (${idle} idle${waiting > 0 ? `, ${waiting} waiting` : ''})`
}

const formatMemoryMb = (usedMb: number, limitMb: number | null) =>
  limitMb != null ? `${usedMb} / ${limitMb} MB` : `${usedMb} MB`

const memoryLevel = (usedMb: number, limitMb: number | null): MetricLevel | null =>
  limitMb != null ? ratioLevel(usedMb, limitMb) : null

const nodeArchiveUrl = (nodeVersion: string): string | null => {
  const normalized = nodeVersion.startsWith('v') ? nodeVersion : `v${nodeVersion}`
  if (!/^v\d+\.\d+\.\d+/.test(normalized)) return null
  return `https://nodejs.org/en/download/archive/${normalized}`
}

const renderNodeVersion = (nodeVersion?: string) => {
  if (!nodeVersion) return '—'
  const url = nodeArchiveUrl(nodeVersion)
  if (!url) return nodeVersion
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {nodeVersion}
    </a>
  )
}

const ProcessMonitoringComponent = () => {
  const [{ lastUpdated, autorefreshRate }, dispatch] = useReducer(reducer, initialState)
  const [getProcessStatus, { loading, result: status }] = useGetProcessStatus()
  const classes = useStyles()

  const refresh = useCallback(async () => {
    await getProcessStatus()
    dispatch({ lastUpdated: new Date() })
  }, [getProcessStatus])

  useAutorefresh(refresh, autorefreshRate * 1000)

  React.useEffect(() => {
    refresh()
  }, [refresh])

  const workers = status?.workers || []
  const instanceGroups = groupWorkersByInstance(workers)
  const showMultiWorker = (status?.workerCount || 0) > 1
  const groupedByInstance = instanceGroups.length > 1
  const singleInstanceHostPlatform =
    instanceGroups.length === 1 ? instanceGroups[0]?.workers[0]?.hostPlatform : undefined

  const renderWorkerRow = (worker: ProcessWorkerStatus) => {
    const cpuMetricLevel = cpuLevel(worker.cpuPercent)
    const rssLevel = memoryLevel(worker.memoryRssMb, worker.memoryRssLimitMb)
    const heapLevel = memoryLevel(worker.memoryHeapMb, worker.memoryHeapLimitMb)
    const eventLoopLevel =
      worker.eventLoopLagP99Ms != null ? ratioLevel(worker.eventLoopLagP99Ms, EVENT_LOOP_REFERENCE_MS) : null

    return (
      <tr key={worker.workerId}>
        <td>{resolveWorkerLabel(worker.workerId, groupedByInstance)}</td>
        <td>{renderNodeVersion(worker.nodeVersion)}</td>
        <td className={cpuMetricLevel ? metricClassName(classes, cpuMetricLevel) : undefined}>
          {worker.cpuPercent != null ? `${worker.cpuPercent}%` : '—'}
        </td>
        <td className={rssLevel ? metricClassName(classes, rssLevel) : undefined}>
          {formatMemoryMb(worker.memoryRssMb, worker.memoryRssLimitMb)}
        </td>
        <td className={heapLevel ? metricClassName(classes, heapLevel) : undefined}>
          {formatMemoryMb(worker.memoryHeapMb, worker.memoryHeapLimitMb)}
        </td>
        <td>{formatUptime(worker.uptimeSec)}</td>
        <td className={eventLoopLevel ? metricClassName(classes, eventLoopLevel) : undefined}>
          {worker.eventLoopLagP99Ms != null ? `${worker.eventLoopLagP99Ms} ms` : '—'}
        </td>
        <td className={metricClassName(classes, handlerLevel(worker))}>
          {worker.handlerRunning} run / {worker.handlerQueued} q
        </td>
        <td>{worker.sqsConsumers}</td>
        <td className={metricClassName(classes, dbPoolLevel(worker))}>{formatDbPool(worker)}</td>
      </tr>
    )
  }

  return (
    <div className={classes.container}>
      <Paper className={classes.statusContainer}>
        {(singleInstanceHostPlatform || showMultiWorker) && (
          <p className={classes.summary}>
            {singleInstanceHostPlatform && <>Host platform: {singleInstanceHostPlatform}. </>}
            {showMultiWorker && (
              <>
                {status?.workerCount} api-server workers
                {groupedByInstance ? ` across ${instanceGroups.length} instances` : ' reporting'}
                {status?.aggregated ? '' : ' (single worker)'}
              </>
            )}
          </p>
        )}

        <table className={classes.table}>
          <thead>
            <tr>
              <th>{groupedByInstance ? 'pm2 worker' : 'Worker'}</th>
              <th>Node</th>
              <th>CPU %</th>
              <th>Memory (RSS)</th>
              <th>Memory (Heap)</th>
              <th>Uptime</th>
              <th>Event loop p99</th>
              <th>Handler</th>
              <th>SQS</th>
              <th>DB pool</th>
            </tr>
          </thead>
          <tbody>
            {instanceGroups.map(group => (
              <React.Fragment key={group.instanceId}>
                {groupedByInstance && (
                  <tr className={classes.instanceHeader}>
                    <td colSpan={10}>
                      Instance {group.instanceId}
                      {group.workers[0]?.hostPlatform ? ` · ${group.workers[0].hostPlatform}` : ''} (
                      {group.workers.length} {group.workers.length === 1 ? 'worker' : 'workers'})
                    </td>
                  </tr>
                )}
                {group.workers.map(renderWorkerRow)}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className={classes.statusFooter}>
          <p>{lastUpdated ? 'Last updated ' + moment(lastUpdated).format('M/D/YYYY h:mm:ss a z') : ''}</p>
          <div className={classes.footerControls}>
            <Select
              value={autorefreshRate}
              autoWidth
              disabled={loading}
              className={classes.outlinedSelectControl}
              classes={{ outlined: classes.outlinedSelect }}
              variant="outlined"
              onChange={event => dispatch({ autorefreshRate: Number(event.target.value) })}
            >
              {AUTO_REFRESH_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <ContainedButton className={classes.refreshButton} disabled={loading} onClick={refresh}>
              Refresh
            </ContainedButton>
          </div>
        </div>
      </Paper>
    </div>
  )
}

export const ProcessMonitoring = () => (
  <PageHeader title="API Worker Processes" subTitle="Node.js process health across all api-server workers">
    <ProcessMonitoringComponent />
  </PageHeader>
)
